/**
 * Help Center category page
 * Displays all articles within a specific category
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  getHelpCategories,
  getHelpArticles,
  getPopularArticles,
  filterArticlesByCategory,
  PopularArticle,
} from '../../services/helpContent';
import { HelpCategory, HelpArticleSummary } from '../../types';
import {
  HelpSearch,
  HelpCategorySidebar,
  HelpArticleCard,
  HelpBreadcrumb,
  HelpCTA,
} from '../../components/help';

export function HelpCategoryPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [articles, setArticles] = useState<HelpArticleSummary[]>([]);
  const [popularArticles, setPopularArticles] = useState<PopularArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Find current category
  const currentCategory = useMemo(() => {
    return categories.find(c => c.id === slug);
  }, [categories, slug]);

  // Filter articles for this category
  const categoryArticles = useMemo(() => {
    if (!slug) return [];
    return filterArticlesByCategory(articles, slug);
  }, [articles, slug]);

  // Load data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [cats, arts, popular] = await Promise.all([
          getHelpCategories().catch(() => []),
          getHelpArticles().catch(() => []),
          getPopularArticles().catch(() => []),
        ]);

        setCategories(cats);
        setArticles(arts);
        setPopularArticles(popular);
        setError(null);
      } catch (err) {
        console.error('Error loading help content:', err);
        setError('Failed to load help content. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Set page title
  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - Help Center - Foxie Systems`;
    } else {
      document.title = 'Help Center - Foxie Systems';
    }
  }, [currentCategory]);

  // Category name lookup for search
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <Link to="/help" className="btn btn-primary mt-4">
            Back to Help Center
          </Link>
        </div>
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Category Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The category you're looking for doesn't exist.
          </p>
          <Link to="/help" className="btn btn-primary">
            Back to Help Center
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container-custom py-8">
          <Link
            to="/help"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Help Center
          </Link>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {currentCategory.name}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
            {currentCategory.description}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <HelpCategorySidebar
            categories={categories}
            popularArticles={popularArticles}
            selectedCategory={slug}
            getCategoryName={getCategoryName}
          />

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {/* Search */}
            <div className="mb-8">
              <HelpSearch
                articles={articles}
                categories={categories}
                placeholder={`Search in ${currentCategory.name}...`}
              />
            </div>

            {/* Breadcrumb */}
            <HelpBreadcrumb
              items={[{ label: currentCategory.name }]}
            />

            {/* Articles */}
            {categoryArticles.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  No articles in this category yet. Check back soon!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {categoryArticles.map(article => (
                  <HelpArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}

            <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
              {categoryArticles.length} article{categoryArticles.length !== 1 ? 's' : ''} in this category
            </p>
          </main>
        </div>

        {/* CTA Section */}
        <HelpCTA />
      </div>
    </div>
  );
}

export default HelpCategoryPage;
